/**
 * THESIS: Earth-observation access begins with place and coverage, not a generic institutional hero.
 * OWN-WORLD: Station Navy infrastructure, Orbital Cyan geography, Action Green controls, and authored synthetic scenes.
 * STORY: Understand Service Portal, inspect representative coverage, learn the request path, then enter the safe demo workspace.
 * FIRST VIEWPORT: Institutional header above a navy split field; the offer and action sit left, a live Philippine coverage map owns the right.
 * FORM: An established-world operational landing surface, derived from the production portal rather than a category template.
 */
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowRight,
  Building2,
  Check,
  CloudSun,
  Database,
  FileCheck2,
  Layers3,
  MapPinned,
  Orbit,
  RadioTower,
  Satellite,
  ScanSearch,
  ShieldCheck,
} from "lucide-react"
import { useState } from "react"

import { DemoNotice } from "@/components/demo-notice"
import { PhilippinesMap } from "@/components/philippines-map"
import { PublicHeader } from "@/components/public-header"
import { ScenePreview } from "@/components/scene-preview"
import { SceneStatusBadge } from "@/components/status-badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  DEMO_PASSWORD,
  formatResolution,
  viewerDefaultPath,
} from "@/lib/domain"
import { guidedExplorerHref } from "@/lib/guided-demo"
import { publicQueryOptions } from "@/lib/query-options"
import { loginFn } from "@/server/functions"

export const Route = createFileRoute("/")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(publicQueryOptions()),
  head: () => ({
    meta: [
      {
        title:
          "Service Portal · Philippine earth observation imagery portal demo",
      },
    ],
  }),
  component: HomePage,
})

function HomePage() {
  const { data } = useSuspenseQuery(publicQueryOptions())
  const { accounts, viewer, featuredScenes } = data
  const analystAccount = accounts.find((account) => account.role === "analyst")
  const guidedDemoMutation = useMutation({
    mutationFn: () => {
      if (!analystAccount) {
        throw new Error("The guided analyst account is unavailable.")
      }

      return loginFn({
        data: {
          email: analystAccount.email,
          password: DEMO_PASSWORD,
        },
      })
    },
    onSuccess: () => {
      window.location.assign(guidedExplorerHref())
    },
  })
  const [selectedSceneId, setSelectedSceneId] = useState(
    featuredScenes.at(0)?.id ?? null
  )
  const selectedScene =
    featuredScenes.find((scene) => scene.id === selectedSceneId) ??
    featuredScenes.at(0)

  return (
    <div className="bg-white text-[var(--ink)]">
      <DemoNotice />
      <PublicHeader viewer={viewer} />

      <main>
        <section className="relative overflow-hidden bg-[var(--navy)] text-white">
          <div className="pointer-events-none absolute inset-y-0 left-[42%] hidden w-px bg-white/10 lg:block" />
          <div className="mx-auto grid min-h-[calc(100svh-123px)] max-w-[1440px] items-center gap-10 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-[0.82fr_1.18fr] lg:gap-12 lg:px-10 lg:py-12">
            <div className="relative z-10 max-w-[620px]">
              <p className="inline-flex items-center gap-2 text-xs font-semibold text-[var(--cyan)]">
                <RadioTower className="size-4" />
                Earth observation for public service
              </p>
              <h1 className="mt-5 max-w-[12ch] text-[clamp(3rem,6.8vw,6rem)] leading-[0.92] font-semibold tracking-[-0.04em] text-balance">
                See the country. Request the right scene.
              </h1>
              <p className="mt-6 max-w-[60ch] text-base leading-7 text-white/72 sm:text-lg sm:leading-8">
                Search Philippine satellite coverage by place, time, sensor, and
                resolution—then move a matching archive scene or new acquisition
                into a traceable request.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button
                  type="button"
                  size="lg"
                  className="bg-[var(--action)] text-white hover:bg-[#24764f]"
                  disabled={
                    guidedDemoMutation.isPending || analystAccount === undefined
                  }
                  onClick={() => guidedDemoMutation.mutate()}
                >
                  {guidedDemoMutation.isPending
                    ? "Preparing guided demo…"
                    : "Start 5-minute guided demo"}
                  <ArrowRight />
                </Button>
                <Button
                  render={<a href="#workflow" />}
                  variant="outline"
                  size="lg"
                  className="border-white/28 bg-transparent text-white hover:bg-white/8 hover:text-white"
                >
                  Follow the request path
                </Button>
              </div>
              {guidedDemoMutation.error ? (
                <p
                  className="mt-3 max-w-[60ch] rounded-[10px] border border-[#f2b8c0] bg-[#fff0f2] px-3 py-2.5 text-sm text-[#8f2d3a]"
                  role="alert"
                >
                  {guidedDemoMutation.error.message}
                </p>
              ) : null}
              <p className="mt-3 max-w-[60ch] text-xs leading-5 text-white/62">
                One click opens a preselected Legazpi archive scene with a
                ready-to-submit synthetic request.{" "}
                <Link
                  to={viewer ? viewerDefaultPath(viewer) : "/login"}
                  className="font-semibold text-white underline decoration-white/35 underline-offset-4 hover:decoration-white"
                >
                  Choose a role or explore freely
                </Link>
                .
              </p>
              <div className="mt-8 grid max-w-[560px] gap-3 border-t border-white/14 pt-5 text-xs leading-5 text-white/62 sm:grid-cols-2">
                <p className="flex gap-2">
                  <ShieldCheck className="mt-0.5 size-4 shrink-0 text-[var(--cyan)]" />
                  No production credentials, imagery, or downloads
                </p>
                <p className="flex gap-2">
                  <Database className="mt-0.5 size-4 shrink-0 text-[var(--cyan)]" />
                  Realistic synthetic catalogue and order states
                </p>
              </div>
            </div>

            <div className="hero-orbit-map relative min-w-0">
              <div className="absolute -top-7 left-5 z-10 hidden items-center gap-2 text-[0.68rem] font-semibold text-white/58 sm:flex">
                <span className="size-2 rounded-full bg-[var(--cyan)] shadow-[0_0_0_5px_rgba(34,182,207,.14)]" />
                Representative demo coverage
              </div>
              <PhilippinesMap
                scenes={featuredScenes}
                selectedSceneId={selectedSceneId}
                onSelectScene={setSelectedSceneId}
                className="min-h-[480px] border border-white/12 shadow-[0_28px_70px_-38px_rgba(0,0,0,.7)] lg:min-h-[620px]"
              />
              {selectedScene ? (
                <div className="relative z-10 mx-3 -mt-4 grid gap-3 rounded-[12px] bg-white p-4 text-[var(--ink)] shadow-[0_18px_42px_-24px_rgba(0,0,0,.75)] sm:mx-6 sm:grid-cols-[1fr_auto] sm:items-center lg:absolute lg:right-6 lg:bottom-6 lg:left-6 lg:mt-0">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold text-[var(--service-blue)]">
                        {selectedScene.satellite}
                      </span>
                      <SceneStatusBadge status={selectedScene.status} />
                    </div>
                    <p className="mt-1 truncate font-semibold text-[var(--navy)]">
                      {selectedScene.location}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {selectedScene.capturedLabel} ·{" "}
                      {formatResolution(selectedScene.resolutionMeters)} ·{" "}
                      {selectedScene.band}
                    </p>
                  </div>
                  <Button
                    render={<Link to="/login" />}
                    variant="outline"
                    size="sm"
                  >
                    Inspect in portal
                    <ArrowRight />
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section
          aria-label="Demonstration catalogue summary"
          className="border-b border-border bg-[var(--cloud)]"
        >
          <div className="mx-auto grid max-w-[1440px] divide-y divide-border px-4 sm:px-6 md:grid-cols-4 md:divide-x md:divide-y-0 lg:px-10">
            {[
              ["12,486", "Synthetic catalogue scenes"],
              ["6", "Representative satellite missions"],
              ["2", "Archive and new-tasking paths"],
              ["100%", "Non-production demo data"],
            ].map(([value, label]) => (
              <div key={label} className="py-5 md:px-6 md:first:pl-0">
                <p className="text-2xl font-semibold tracking-[-0.025em] text-[var(--navy)]">
                  {value}
                </p>
                <p className="mt-1 text-xs font-medium text-muted-foreground">
                  {label}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section id="about" className="scroll-mt-24">
          <div className="mx-auto grid max-w-[1440px] gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[0.82fr_1.18fr] lg:px-10 lg:py-28">
            <div>
              <p className="text-xs font-semibold text-[var(--service-blue)]">
                Public-service imagery infrastructure
              </p>
              <h2 className="mt-3 max-w-[14ch] text-[clamp(2.2rem,4vw,4rem)] leading-[1] font-semibold tracking-[-0.035em] text-balance text-[var(--navy)]">
                One request trail from ground station to government work.
              </h2>
            </div>
            <div className="max-w-[760px]">
              <p className="text-xl leading-8 font-medium tracking-[-0.012em] text-[var(--ink)] sm:text-2xl sm:leading-9">
                Service Portal connects a Philippine earth-observation archive
                with the operational decisions required to release the right
                imagery to an authorized agency.
              </p>
              <p className="mt-5 max-w-[68ch] leading-7 text-muted-foreground">
                The referenced production system brings together
                area-of-interest search, optical and radar filters, archive
                selection, new satellite tasking, allocation-aware downloads,
                and a separate administration queue. This rebuild preserves that
                operating model while replacing every identity and record with
                synthetic content.
              </p>
              <div className="mt-8 grid gap-x-8 gap-y-5 sm:grid-cols-2">
                {[
                  [
                    CloudSun,
                    "Disaster response",
                    "Pre- and post-event coverage for situational assessment.",
                  ],
                  [
                    Layers3,
                    "Environmental monitoring",
                    "Repeat observations for land, coast, crops, and habitat.",
                  ],
                  [
                    MapPinned,
                    "Planning and mapping",
                    "Current geographic evidence for public infrastructure work.",
                  ],
                  [
                    ScanSearch,
                    "Research and analysis",
                    "Sensor-specific scenes for reproducible remote-sensing studies.",
                  ],
                ].map(([Icon, title, description]) => {
                  const UseIcon = Icon as typeof CloudSun
                  return (
                    <div key={String(title)} className="flex gap-3">
                      <span className="grid size-10 shrink-0 place-items-center rounded-[10px] bg-[#e8f4f7] text-[var(--service-blue)]">
                        <UseIcon className="size-[1.1rem]" />
                      </span>
                      <div>
                        <h3 className="font-semibold text-[var(--navy)]">
                          {String(title)}
                        </h3>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                          {String(description)}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>

        <section
          id="imagery"
          className="scroll-mt-24 bg-[var(--navy)] text-white"
        >
          <div className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-24">
            <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
              <div>
                <p className="text-xs font-semibold text-[var(--cyan)]">
                  Catalogue evidence
                </p>
                <h2 className="mt-3 max-w-[16ch] text-[clamp(2.2rem,4vw,3.8rem)] leading-[1] font-semibold tracking-[-0.035em] text-balance">
                  Different sensors answer different questions.
                </h2>
              </div>
              <p className="max-w-[52ch] text-sm leading-6 text-white/64">
                These authored previews are illustrative only. The metadata
                structure mirrors the decisions an analyst makes before
                requesting imagery.
              </p>
            </div>

            <div className="mt-10 grid gap-4 lg:grid-cols-3">
              {featuredScenes.map((scene, index) => (
                <article
                  key={scene.id}
                  className="group overflow-hidden rounded-[14px] border border-white/12 bg-white/[0.055]"
                >
                  <ScenePreview
                    scene={scene}
                    className="aspect-[16/10] rounded-none"
                  />
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-xs font-semibold text-[var(--cyan)]">
                        {scene.satellite} · {scene.type}
                      </span>
                      <span className="text-[0.65rem] text-white/60">
                        0{index + 1} / 03
                      </span>
                    </div>
                    <h3 className="mt-3 text-xl font-semibold tracking-[-0.02em]">
                      {scene.location}
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-white/62">
                      {scene.description}
                    </p>
                    <dl className="mt-5 grid grid-cols-3 gap-3 border-t border-white/12 pt-4">
                      <div>
                        <dt className="text-[0.68rem] text-white/60">
                          Resolution
                        </dt>
                        <dd className="mt-1 text-xs font-semibold">
                          {formatResolution(scene.resolutionMeters)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[0.68rem] text-white/60">Cloud</dt>
                        <dd className="mt-1 text-xs font-semibold">
                          {scene.cloudCover === null
                            ? "N/A · SAR"
                            : `${scene.cloudCover}%`}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[0.68rem] text-white/60">
                          Product
                        </dt>
                        <dd className="mt-1 text-xs font-semibold">
                          {scene.productLevel}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          id="workflow"
          className="scroll-mt-24 border-b border-border bg-[var(--cloud)]"
        >
          <div className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
            <div className="grid gap-12 lg:grid-cols-[0.65fr_1.35fr]">
              <div>
                <p className="text-xs font-semibold text-[var(--service-blue)]">
                  Search to release
                </p>
                <h2 className="mt-3 text-[clamp(2.2rem,4vw,3.8rem)] leading-[1] font-semibold tracking-[-0.035em] text-[var(--navy)]">
                  A request path with no missing state.
                </h2>
                <p className="mt-5 max-w-[48ch] leading-7 text-muted-foreground">
                  The demo keeps the production-shaped handoffs visible so a
                  reviewer can follow the user and operator sides of the same
                  request.
                </p>
              </div>
              <ol className="divide-y divide-border border-y border-border">
                {[
                  {
                    icon: MapPinned,
                    title: "Define the area",
                    body: "Search a Philippine place or use the map to describe an area of interest in spatial and textual form.",
                  },
                  {
                    icon: Satellite,
                    title: "Match the sensor",
                    body: "Filter archive coverage by date, satellite, optical or SAR type, resolution, cloud, band, and polarization.",
                  },
                  {
                    icon: FileCheck2,
                    title: "State the intended use",
                    body: "Select existing scenes or specify new tasking, then record the public-sector purpose and constraints.",
                  },
                  {
                    icon: RadioTower,
                    title: "Review, prepare, release",
                    body: "Operations validates the request, updates status, prepares the package, and records allocation and expiry.",
                  },
                ].map((step, index) => {
                  const Icon = step.icon
                  return (
                    <li
                      key={step.title}
                      className="grid gap-4 py-6 sm:grid-cols-[56px_1fr_auto] sm:items-start"
                    >
                      <span className="grid size-11 place-items-center rounded-[10px] bg-[var(--navy)] text-[var(--cyan)]">
                        <Icon className="size-5" />
                      </span>
                      <div>
                        <h3 className="text-lg font-semibold tracking-[-0.015em] text-[var(--navy)]">
                          {step.title}
                        </h3>
                        <p className="mt-1 max-w-[66ch] text-sm leading-6 text-muted-foreground">
                          {step.body}
                        </p>
                      </div>
                      <span className="text-xs font-semibold text-[var(--telemetry)]">
                        0{index + 1}
                      </span>
                    </li>
                  )
                })}
              </ol>
            </div>
          </div>
        </section>

        <section id="service-guide" className="scroll-mt-24">
          <div className="mx-auto grid max-w-[1440px] gap-8 px-4 py-20 sm:px-6 lg:grid-cols-[1fr_0.9fr] lg:px-10 lg:py-28">
            <Card className="rounded-[14px] border border-border bg-white p-0 shadow-none ring-0">
              <div className="grid gap-8 p-6 sm:p-8 lg:grid-cols-[0.7fr_1.3fr]">
                <div>
                  <span className="grid size-12 place-items-center rounded-[12px] bg-[#e8f4f7] text-[var(--service-blue)]">
                    <Building2 />
                  </span>
                  <h2 className="mt-5 text-2xl font-semibold tracking-[-0.025em] text-[var(--navy)]">
                    What a complete request carries
                  </h2>
                </div>
                <ul className="grid gap-3 text-sm leading-6 text-[var(--ink)]">
                  {[
                    "Area of interest: place names, coordinates, or a geospatial file concept",
                    "Archive date range or preferred new-acquisition window",
                    "Optical bands or SAR polarization when the analysis requires them",
                    "Spatial-resolution and cloud-cover constraints",
                    "Government program, research question, or operational use",
                  ].map((item) => (
                    <li key={item} className="flex gap-3">
                      <Check className="mt-1 size-4 shrink-0 text-[var(--action)]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Card>

            <div className="rounded-[14px] bg-[var(--navy)] p-6 text-white sm:p-8">
              <div className="flex items-center gap-3">
                <span className="grid size-11 place-items-center rounded-[10px] bg-white/8 text-[var(--cyan)]">
                  <Orbit />
                </span>
                <p className="text-xs font-semibold text-[var(--cyan)]">
                  Ready to inspect the system?
                </p>
              </div>
              <h2 className="mt-6 max-w-[16ch] text-3xl leading-[1.05] font-semibold tracking-[-0.03em]">
                Use the analyst account, then switch to operations.
              </h2>
              <p className="mt-4 max-w-[52ch] text-sm leading-6 text-white/64">
                Both demo identities are listed at sign-in. Create an order as
                the analyst, then move it through the queue as the
                administrator.
              </p>
              <Button
                render={<Link to="/login" />}
                size="lg"
                className="mt-7 bg-white text-[var(--navy)] hover:bg-[#edf5f5]"
              >
                View demo credentials
                <ArrowRight />
              </Button>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border bg-[var(--cloud)]">
        <div className="mx-auto grid max-w-[1440px] gap-8 px-4 py-10 sm:px-6 md:grid-cols-[1fr_auto] md:items-end lg:px-10">
          <div>
            <p className="font-semibold text-[var(--navy)]">
              Service Portal portfolio demonstration
            </p>
            <p className="mt-2 max-w-[68ch] text-xs leading-5 text-muted-foreground">
              An independent synthetic rebuild for demonstration purposes. It
              does not provide production access, represent current service
              policy, or distribute satellite imagery.
            </p>
          </div>
          <div className="text-xs text-muted-foreground md:text-right">
            <p>Department and program names retained for product context.</p>
            <p className="mt-1">Demo build · July 2026</p>
            <a
              href="https://jericop.dev"
              rel="author"
              className="mt-2 inline-flex font-semibold text-[var(--navy)] underline decoration-border underline-offset-4 hover:decoration-[var(--navy)]"
            >
              Built by Jerico Pulvera
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}
